
import { Box, Modal, Text } from '@fozito/common-ui';
import { set, sumBy } from 'lodash';
import { cashierCtrl } from 'modules/POS/Cashier/cashier.controller';
import { useDollarAmountInputRef } from 'modules/POS/Cashier/components/DollarAmount/Input';
import { useSocketContext } from 'modules/POS/Cashier/hooks/useSocket';
import { ICustomerPaymentDetail, MultipleCardTypePassData, PaymentSocketData, TypePaymentActors, TypePaymentPassData } from 'modules/POS/Cashier/socket.interface';
import useConfirmTipTicket from 'modules/POS/PaymentDetails/ConfirmTipTicket';
import { useTicketWrapper } from 'modules/POS/Receipts/context';
import cashierService from 'modules/POS/services';
import cashierApis from 'modules/POS/services/apis';
import { IBillInfoResData } from 'modules/POS/services/types/bill';
import { IApiMultipleCardsPaymentBody } from 'modules/POS/services/types/payment';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { IResponseData } from 'services/response';
import { Message } from 'stompjs';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { removeBase64Prefix } from 'utils/removeBase64Prefix';
import storage from 'utils/sessionStorage';
import { PAYMENT_PASS_DATA_TOPIC } from 'utils/socket';
import CreditCardLoading, { ICreditPaymentLoadingRef } from '../../Card/ModalCardPayment/PaymentLoading';
import RequestSignature, { ISignatureRef } from '../../Card/ModalCardPayment/RequestSignature';
import DescribePayment from './DescribePayment';
import DetailPayment from './DetailPayment';
import { IPayMultiCardResData } from './index.interface';
import PaymentKeyboard from './PaymentKeyboard';
import { IUICashierFormValues } from 'modules/POS/Cashier/cashier.interface';
import useSMSEmailSignature from 'modules/POS/Cashier/hooks/useSMSEmailSignature';
import useListenRequestSync from 'modules/POS/Cashier/hooks/useListenRequestSync';
type Props = {
  enableCardFee?: IUICashierFormValues['chargeFee'];
  feeCreditCard?: IUICashierFormValues['feeCreditCard'];
  feeCreditCardAmount?: IUICashierFormValues['feeCreditCardAmount'];
};
type Ref = {
  open: (billId: string) => void;
};
export const useCashierMultipleCardRef = () => useRef<Ref>(null);
const CashierMultipleCard = forwardRef<Ref, Props>(({ enableCardFee, feeCreditCardAmount }, ref) => {
  const [completedTicketModalRef, completedTicketModalContext] = useConfirmTipTicket();
  const dispatch = useAppDispatch();
  const socketContext = useSocketContext();
  const ticketContext = useTicketWrapper();

  const creditCardLoadingRef = useRef<ICreditPaymentLoadingRef>(null);
  const dollarRef = useDollarAmountInputRef();
  const triggerClear = useRef(false);
  const signatureRef = useRef<ISignatureRef>(null);
  const paymentItemInfoRef = useRef<{ id: string, isDone: boolean }>({ id: '', isDone: false });
  const [isDone, setDone] = useState(false);
  const SMSEmailSignature = useSMSEmailSignature();

  const cashierMsg = cashierService.selectors.cashierMsg();
  const cards = cashierService.selectors.cardsByTransaction();
  const [amountKB, setAmountKB] = useState<number | null>();

  const [detail, setOpen] = useState<IBillInfoResData | null>(null);

  const totalAmount = (detail?.total || 0) + (feeCreditCardAmount || 0);

  const fetchDetail = async (_billId: string) => {
    try {
      const res: IResponseData<IBillInfoResData> = await cashierApis.getBillById(_billId);
      const detail = res.data.data;
      if (res.data.data) {
        cashierCtrl.switchTicketWithDetailData(detail);
        socketContext.send(PAYMENT_PASS_DATA_TOPIC, {
          billId: detail.billId,
          actor: TypePaymentActors.CASHIER,
          action: TypePaymentPassData.PASS_DATA,
          data: {
            cash: 0,
            remaining: 0,
            chanceCash: 0,
            loyalty: 0,
            giftCard: 0,
            checkOthers: 0,
            card: 0,
            multiPay: 0,
            multiCards: 1,
            totalValue: detail.total + (feeCreditCardAmount || 0),
            subTotal: detail.subTotal || 0,
            cardFee: feeCreditCardAmount || 0,
            saleTax: detail.saleTax || 0,
            usedTax: detail.useTax || 0,
            discount: (detail.discount || 0) + (detail.discountItem || 0),
          } as ICustomerPaymentDetail,
        });
        return res.data.data;
      }
      return null;
    } catch (error) {
      return null;
    }
  };
  useListenRequestSync(async (_billId) => {
    if (!detail) return;
    await fetchDetail(_billId);
  });

  const onOpen: Ref['open'] = async (_billId) => {
    socketContext.switchCusPayment(_billId);
    dispatch(cashierService.actions.async.getCardsByTransactionId.fetch(_billId));
    try {
      const detail = await fetchDetail(_billId);
      if (detail) {
        setOpen(detail);
      }
    } catch (error) {
    }
  };

  const onCompleted = (ignorePrint?: boolean) => {
    signatureRef.current?.close();
    if (!detail?.billId) return;
    socketContext.alertPaymentSuccess(detail.billId);
    const callback = () => {
      dispatch(cashierService.actions.saveCashierMessage(null));
      cashierCtrl.generateNewTicketAfterPayment();
      setOpen(null);
    };
    if (!completedTicketModalRef.current) {
      callback();
      if (!ignorePrint) {
        ticketContext.completedTicket(detail?.billId, true);
      }
      return;
    }
    completedTicketModalRef.current.open(detail?.billId, callback, ignorePrint);
  };


  const remaining = useMemo(() => {
    const totalPayment = sumBy(Object.values(cards), o => (o?.amount ?? 0));
    const value = totalAmount - totalPayment;
    return value > 0 ? +(value?.toFixed(2)) : 0;
  }, [cards, detail, feeCreditCardAmount]);

  useEffect(() => {
    if (triggerClear.current) {
      triggerClear.current = false;
      return;
    }
    const amountStr = remaining?.toFixed(2) || '';
    dollarRef.current?.init(remaining ? amountStr : '');
  }, [cards, detail, feeCreditCardAmount]);

  useEffect(() => {
    if (!cashierMsg) return;
    listeningData(cashierMsg);
  }, [cashierMsg]);

  const sendingData = (type: string, data?: any) => {
    const body: PaymentSocketData = {
      storeId: storage.shop_id.get(),
      billId: detail?.billId || '',
      actor: TypePaymentActors.CASHIER,
      data,
    };
    switch (type) {
      case MultipleCardTypePassData.REQUEST_TIP: {
        set(body, 'action', MultipleCardTypePassData.REQUEST_TIP);
        break;
      }
      case MultipleCardTypePassData.PASS_TIP_VALUE_ON_TYPING: {
        set(body, 'action', MultipleCardTypePassData.PASS_TIP_VALUE_ON_TYPING);
        break;
      }
      case MultipleCardTypePassData.PAYMENT_PROCESSING: {
        set(body, 'action', MultipleCardTypePassData.PAYMENT_PROCESSING);
        break;
      }
      case MultipleCardTypePassData.PAYMENT_PROCESSING_DONE: {
        set(body, 'action', MultipleCardTypePassData.PAYMENT_PROCESSING_DONE);
        break;
      }
      case MultipleCardTypePassData.UPDATE_SIGNATURE_COMPLETED: {
        set(body, 'action', MultipleCardTypePassData.UPDATE_SIGNATURE_COMPLETED);
        break;
      }
      case MultipleCardTypePassData.REQUEST_SIGNATURE: {
        set(body, 'action', MultipleCardTypePassData.REQUEST_SIGNATURE);
        break;
      }
      case MultipleCardTypePassData.CUSTOMER_RECEIVE_CONFIGS: {
        set(body, 'action', MultipleCardTypePassData.CUSTOMER_RECEIVE_CONFIGS);
        break;
      }
      default:
        break;
    }

    socketContext.send(PAYMENT_PASS_DATA_TOPIC, body);
  };

  const listeningData = (message: Message) => {
    if (!message.body) return;
    const payment: PaymentSocketData<string> = JSON.parse(message.body);
    if (!payment) return;
    if (payment.billId !== detail?.billId) return;
    if (payment.actor !== TypePaymentActors.CUSTOMER) return;

    switch (payment.action) {
      case MultipleCardTypePassData.REFRESH_DETAIL: {
        // dispatch(multiCardActions.refreshDetail.fetch(detail?.billId || ''));
        break;
      }
      case MultipleCardTypePassData.UPDATE_SIGNATURE_COMPLETED: {
        dispatch(cashierService.actions.async.getCardsByTransactionId.fetch(detail?.billId || ''));
        signatureRef.current?.close();
        break;
      }
      case MultipleCardTypePassData.PAYMENT_COMPLETED: {
        signatureRef.current?.close();
        onCompleted();
        break;
      }
      case MultipleCardTypePassData.ON_CUSTOMER_SIGNATURE_CHANGE: {
        signatureRef.current?.setValue(payment.data);
        break;
      }
      case MultipleCardTypePassData.COMPLETED_PAYMENT_WITHOUT_RECEIPT: {
        signatureRef.current?.close();
        onCompleted(true);
        break;
      }
      default:
        break;
    }
  };

  const onBack = () => {
    setOpen(null);
    socketContext.backToCheckIn();
  };

  const onPostPayment = async () => {
    const isDone = (remaining - (amountKB || 0)) <= 0;
    if (!detail?.billId) return;
    creditCardLoadingRef.current?.open(amountKB || 0, 120000);
    sendingData(MultipleCardTypePassData.PAYMENT_PROCESSING, { amount: amountKB || 0 });
    const body: IApiMultipleCardsPaymentBody = { billId: detail?.billId, amount: amountKB || 0, done: isDone, feeCredit: feeCreditCardAmount, chargeFee: enableCardFee };
    try {
      const res: IResponseData<IPayMultiCardResData> = await cashierApis.paymentMultipleCards(body);
      const resData = res?.data?.data;
      if (resData) {
        const socketPayload = { id: resData.id, isDone: isDone };
        paymentItemInfoRef.current.id = socketPayload.id;
        paymentItemInfoRef.current.isDone = socketPayload.isDone;
        setDone(socketPayload.isDone);
        sendingData(MultipleCardTypePassData.REQUEST_SIGNATURE, socketPayload);
        dispatch(cashierService.actions.async.getCardsByTransactionId.fetch(detail.billId));
        signatureRef.current?.open();
      } else {
        sendingData(MultipleCardTypePassData.PAYMENT_PROCESSING_DONE);
      }
    } catch (error) {
      sendingData(MultipleCardTypePassData.PAYMENT_PROCESSING_DONE);
    } finally {
      creditCardLoadingRef.current?.setVisible(false);
    }
  };

  const handleClear = () => {
    triggerClear.current = true;
    dollarRef.current?.focus('');
    setAmountKB(0);
  };

  const onSignatureDone = async (signatureValue: string) => {
    const idCreditCard = paymentItemInfoRef?.current?.id;
    if (!idCreditCard) return;
    signatureRef.current?.close();
    try {
      const res: IResponseData<boolean> = await cashierApis.updateSignatureForCreditCard(idCreditCard || '', removeBase64Prefix(signatureValue));
      if (res.data.data) {
        if (!paymentItemInfoRef?.current?.isDone) {
          sendingData(MultipleCardTypePassData.UPDATE_SIGNATURE_COMPLETED, {});
          dispatch(cashierService.actions.async.getCardsByTransactionId.fetch(detail?.billId || ''));
        } else {
          onCompleted();
        }
        return;
      }
    } catch (error) { }
    finally {
    }
  };

  useImperativeHandle(ref, () => ({
    open: onOpen,
  }));

  return (
    <Modal destroyOnClose title='' open={!!detail} width={'auto'} styles={{ header: { display: 'none' }, body: { maxHeight: 'unset' } }}>
      <PageStyled v2>
        <PaymentFormStyled className={'v2'}>
          <PaymentInfoBoxStyled>
            <Box display={'flex'} className='box-payment'>
              <Box width={'700px'} overflow="auto" display='flex' flexDirection='column'>
                <Box background={'#232F3E'} className="center" height={'4rem'} display='flex' justifyContent='center' alignItems='center'>
                  <Text title_h1_56_medium style={{ color: '#FFF', fontSize: 30 }}>
                    MULTI-CARDS
                  </Text>
                </Box>
                <PaymentKeyboard
                  inputRefV2={dollarRef}
                  onBack={onBack}
                  onDone={onCompleted}
                  onPay={onPostPayment}
                  onClear={handleClear}
                  value={amountKB}
                  remaining={remaining}
                  onChange={setAmountKB}
                  total={totalAmount}
                  cards={cards}
                  billId={detail?.billId}
                />
              </Box>
              <RightPayment className={'v2'}>
                <DescribePayment v2 data={detail} cardFee={feeCreditCardAmount} total={totalAmount} />
                <DetailPayment v2 cards={cards} remaining={remaining} />
              </RightPayment>
            </Box>
          </PaymentInfoBoxStyled>
          <RequestSignature ref={signatureRef}
            onAgree={onSignatureDone}
            onNoReceipt={isDone ? SMSEmailSignature.onNoReceipt(detail?.billId || '', () => onCompleted(true)) : undefined}
            onEmail={isDone ? SMSEmailSignature.onEmail(detail?.billId || '', () => onCompleted(true)) : undefined}
            onSMS={isDone ? SMSEmailSignature.onSMS(detail?.billId || '', () => onCompleted(true)) : undefined}
            defaultSMSPhone={detail?.customerPhone}
          />
          <CreditCardLoading onCancel={onBack} ref={creditCardLoadingRef} />
        </PaymentFormStyled>
      </PageStyled>
      {completedTicketModalContext}
    </Modal>
  );
});
CashierMultipleCard.displayName = 'CashierMultipleCard';
export default CashierMultipleCard;

const RightPayment = styled.div`
display: flex;
flex-direction: column;
width: 19rem;
overflow: auto;
&.v2 {
  width: 450px;
  background: #f5f5f5; 
  border-left: 1px solid #CCD4DC;
  .space-between {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}
`;

const PageStyled = styled.div<{ v2?: boolean }>`
    min-height: 100vh;
    background: var(--fill-fill-3, #E5E5E5);
    display: flex;
    flex-direction: column;

    ${({ v2 }) => v2 && `
      min-height: unset;
      background: #E5E5E5;
    `}
`;

export const PaymentFormStyled = styled.div`
    display: flex; 
    justify-content: center;
    align-items: start;
    flex-grow:1;
    padding: 2rem;

    &.v2 {
      padding: 0;
    }
`;


const PaymentInfoBoxStyled = styled.div`
  .box-payment {
    background: #fff;
    border-radius: 5px;
    opacity: 0.9;
    background: var(--fill-fill-0, #FFF);
    box-shadow: -1px 1px 4px 0px rgba(0, 0, 0, 0.10) inset, 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  }
  display: flex;
  .divider {
    background: #86909C;
    width: 100%;
    height: 1px;
  }
`;